import axios, {AxiosInstance, AxiosResponse} from 'axios';
import {API_BASE_URL} from "./ApiClientConfig";
import {
    GetUserAgreementResponse,
    GetUserSettingsResponse,
    UpdateUserAgreementRequest,
    UpdateUserSettingsRequest,
    User
} from "../models/User";
import {UserAppointment} from "../models/UserAppointment";
import {Appointment} from "../models/Appointment";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import TimeType from "../models/TimeType";
import {getShortenDateTimeWithYear} from "../utils/Utils";
import {
    ACCESS_TOKEN_LOCAL_STORAGE_KEY,
    OAUTH_CALLBACK_URL,
    REFRESH_TOKEN_LOCAL_STORAGE_KEY
} from "../constants/constants";
import AttendType from "../models/AttendType";
import Member from "../models/Member";
import {AccessTokenAndRefreshToken} from "../models/AccessTokenAndRefreshToken";
import {JwtUtils} from "../utils/JwtUtils";
import {KakaoScopes} from "../models/KakaoScopes";
import {MemberPollResponse} from "../models/response/poll";
import {PollSingleRequest} from "../models/request/poll";
import {JoinAppointmentRequest} from "../models/request/member";
import {Page} from "../models/Page";
import {Notice} from "../models/notice";

const alfyApiClient: AxiosInstance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * 리프레시 토큰 관련 인터셉터
 * accessToken의 exp가 만료된 경우 토큰 리프레시 요청을 보낸다
 * 성공시 axios의 Authorization 헤더를 새 토큰으로 변경
 * 실패시 logout
 */
alfyApiClient.interceptors.request.use(async (config) => {
    const accessToken = config.headers.Authorization as string | undefined;

    // 액세스토큰이 없는 경우 없는 대로 사용, 있고 만료도 안됐다면 그대로 사용.
    if (!accessToken || !JwtUtils.isExpired(accessToken)) {
        return config;
    }

    const authTokens = await getNewAuthTokens();
    if (authTokens == null) {
        window.location.href = "/";
        return config;
    }
    setClientAuthToken(authTokens)

    return config;
})

export const setClientAccessToken = (token: string) => {
    alfyApiClient.defaults.headers.common.Authorization = `bearer ${token}`;
}

export const setClientAuthToken = ({accessToken, refreshToken}: AccessTokenAndRefreshToken) => {
    alfyApiClient.defaults.headers.common.Authorization = `bearer ${accessToken}`;
    localStorage.setItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_LOCAL_STORAGE_KEY, refreshToken);
}

export const resetClientAuthToken = () => {
    alfyApiClient.defaults.headers.common.Authorization = null;
    localStorage.removeItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_LOCAL_STORAGE_KEY);
}

/**
 * 이메일, 패스워드 로그인. 당장은 카카오 로그인만 사용해서 쓰이는 곳 없음.
 * @param email
 * @param password
 */
export const login = async (email: string, password: string): Promise<string> => {
    const loginResponse: AxiosResponse<AccessTokenAndRefreshToken> = await alfyApiClient
        .post('/login', {'email': email, 'password': password});
    if (loginResponse.status !== 200) {
        console.log("login failed."); // TODO implement
    }

    const res = loginResponse.data;
    setClientAuthToken(res);

    return res.accessToken;
};

export const kakaoLogin = async (code: string): Promise<AccessTokenAndRefreshToken | null> => {
    try {
        const loginResponse: AxiosResponse<AccessTokenAndRefreshToken> = await alfyApiClient
            .post('/v1/users/login/kakao', {'code': code, 'redirectUri': OAUTH_CALLBACK_URL})

        const res = loginResponse.data;
        setClientAuthToken(res);
        return res;
    } catch (error) {
        console.error('Kakao login failed', error);
        return null;
    }
}

/**
 * 로그아웃은 단순히 state와 localStorage의 authToken 정보를 없애는 것으로 처리한다.
 * 호출부에서 userState를 비우고, 로그인 페이지로 이동시켜야 함
 */
export const logout = () => {
    resetClientAuthToken();
}

export const me = async (): Promise<User | null> => {
    try {
        const meResponse = await alfyApiClient
            .get('/v1/users/me');

        // 프로필 이미지가 없는 경우, 기본 프로필 이미지를 넣어 userState에 저장한다.
        if (!('profileImage' in meResponse.data) || meResponse.data.profileImage === null) {
            meResponse.data['profileImage'] = "/public/default_profile_image.jpg"
        }
        return meResponse.data as User;
    } catch (e: any) {
        if (e.response?.status === 401) {
            console.log("userme failed. reason: unauthorized");
            return null;
        }

        console.error('userme failed', e);
        return null;
    }
}

/**
 * 리프레시 토큰으로 새로운 토큰 반환
 * 리프레시 토큰이 없거나 만료된 경우, 또는 서버 요청이 실패한 경우 null 반환
 */
export const getNewAuthTokens = async (): Promise<AccessTokenAndRefreshToken | null> => {
    const refreshToken = JwtUtils.getTokenFromLocalStorage(REFRESH_TOKEN_LOCAL_STORAGE_KEY)
    if (refreshToken === null) {
        return null;
    }

    try {
        const refreshResponse: AxiosResponse<AccessTokenAndRefreshToken> = await alfyApiClient.get(
            '/v1/users/refresh-token',
            {
                headers: {
                    Authorization: `bearer ${refreshToken}`
                }
            }
        );

        return refreshResponse.data;
    } catch (error) {
        console.error('Token refresh failed', error);
        return null;
    }
}

/**
 * 카카오 유저의 동의항목을 반환
 * 해당 항목을 기반으로 추가 동의 받을지 여부를 결정한다
 */
export const getUserKakaoScope = async (): Promise<KakaoScopes> => {
    const response: AxiosResponse<KakaoScopes> = await alfyApiClient.get('/v1/users/kakao-scopes');
    return response.data;
}

export const getAppointment = async (id: string): Promise<Appointment> => {
    const response = await alfyApiClient.get(`/v1/appointments/${id}`)
    return response.data as Appointment;
}

export const getAppointmentWithMemberPollItems = async (id: string): Promise<Appointment> => {
    const response = await alfyApiClient.get(`/v1/appointments/${id}:with-poll`)
    return Appointment.fromJson(response.data);
}

export const getUserAppointments = async (): Promise<UserAppointment[]> => {
    const response = await alfyApiClient.get('/v1/user-appointments');
    return response.data.map((appointment: any) => UserAppointment.fromJson(appointment));
};

const getAppointmentDefaultName = (currentDate: dayjs.Dayjs) => `${getShortenDateTimeWithYear(currentDate.toDate())}에 생성한 약속잡기`;

export const requestCreateAppointment = async (selectedDuration: string, selectedTimeType: TimeType, selectedReminder: string): Promise<Appointment> => {
    const currentDate = dayjs().tz('Asia/Seoul')

    function calculatePollItemEndDate(): string {
        if (selectedDuration === "1w") {
            return currentDate.add(7, 'day')
                .format().split('T')[0];
        }
        if (selectedDuration === "2w") {
            return currentDate.add(14, 'day')
                .format().split('T')[0];
        }
        if (selectedDuration === "1m") {
            return currentDate.add(1, 'month')
                .format().split('T')[0];
        }
        if (selectedDuration === "2m") {
            return currentDate.add(2, 'month')
                .format().split('T')[0];
        }

        return currentDate.format().split('T')[0]
    }

    function calculateDeadline(): string {
        if (selectedReminder === "6h") {
            return currentDate.add(6, 'hour')
                .format()
                .split('+')[0];
        }
        if (selectedReminder === "today") {
            return currentDate
                .set('hour', 23)
                .set('minute', 59)
                .set('second', 59)
                .format()
                .split('+')[0];
        }
        if (selectedReminder === "tomorrow") {
            return currentDate.add(1, 'day')
                .set('hour', 23)
                .set('minute', 59)
                .set('second', 59)
                .format()
                .split('+')[0];
        }
        if (selectedReminder === "dayAfterTomorrow") {
            return currentDate.add(2, 'day')
                .set('hour', 23)
                .set('minute', 59)
                .set('second', 59)
                .format()
                .split('+')[0];
        }

        throw Error("Invalid reminder option");
    }

    function calculateReminderTime(): string {
        if (selectedReminder === "6h") {
            return currentDate.add(6, 'hour')
                .subtract(3, 'hour')
                .format()
                .split('+')[0];
        }
        if (selectedReminder === "today") {
            return currentDate
                .set('hour', 21)
                .set('minute', 0)
                .set('second', 0)
                .subtract(3, 'hour')
                .format()
                .split('+')[0];
        }
        if (selectedReminder === "tomorrow") {
            return currentDate.add(1, 'day')
                .set('hour', 21)
                .set('minute', 0)
                .set('second', 0)
                .format()
                .split('+')[0];
        }
        if (selectedReminder === "dayAfterTomorrow") {
            return currentDate.add(2, 'day')
                .set('hour', 21)
                .set('minute', 0)
                .set('second', 0)
                .format()
                .split('+')[0];
        }

        throw Error("Invalid reminder option")
    }

    const response = await alfyApiClient
        .post('/v1/appointments', {
            "name": getAppointmentDefaultName(currentDate), // TODO 자동생성
            "type": "CASUAL", // TODO
            "timeType": selectedTimeType,
            "pollItemStartDate": currentDate.format().split('T')[0],
            "pollItemEndDate": calculatePollItemEndDate(),
            "deadline": calculateDeadline(),
            "reminderTimes": [calculateReminderTime()],
            "comment": null,
        });

    return Appointment.fromJson(response.data);
}

export const getMember = async (appointmentId: string): Promise<Member | null> => {
    try {
        const response = await alfyApiClient
            .get(`/v1/appointments/${appointmentId}:get-member`)

        return Member.fromJson(response.data)
    } catch (e: any) {
        if (e.response?.status === 404) {
            return null;
        }
        throw e;
    }
}

export const requestJoinAppointment = async (request: JoinAppointmentRequest): Promise<Member> => {
    const response = await alfyApiClient
        .post(`/v1/appointments/${request.appointmentId}/members`, {
            name: request.name,
            role: request.role,
        })

    return response.data;
}

export const getNotice = async (id: string) => {
    const response = await alfyApiClient.get<Notice>(`/v1/notices/${id}`)
    return response.data;
}

export const getNotices = async (page: number, pageSize: number) => {
    const response = await alfyApiClient.get<Page<Notice>>('/v1/notices', {
        params: {
            page: page,
            pageSize: pageSize
        }
    })
    return response.data;
}

export const fetchUserMeSettings = async () => {
    const response = await alfyApiClient.get<GetUserSettingsResponse>(`/v1/users/me/settings`)
    return response.data
}

export const fetchUserMeAgreement = async () => {
    const response = await alfyApiClient.get<GetUserAgreementResponse>(`/v1/users/me/agreements`)
    return response.data
}

export const updateUserSettings = async (userId: string, request: UpdateUserSettingsRequest) => {
    const response = await alfyApiClient.patch(`/v1/users/${userId}/settings`, request)
    return response.data;
}

export const updateUserAgreement = async (userId: string, request: UpdateUserAgreementRequest) => {
    const response = await alfyApiClient.patch<GetUserAgreementResponse>(`/v1/users/${userId}/agreements`, request)
    return response.data;
}

// TODO 적절한 위치로 옮기자
export interface PollPayload {
    pollItemId: string
    attendType: AttendType
    comment?: string
}

export const poll = async (appointmentId: string, payload: PollPayload[]): Promise<void> => {
    await alfyApiClient.post(`/v1/appointments/${appointmentId}/polls:multiple`,
        { polls: payload }
    )
}

export const pollSingle = async (request: PollSingleRequest) => {
    return await alfyApiClient.post<MemberPollResponse>(`/v1/appointments/${request.appointmentId}/polls/${request.pollItemId}`,
        {
            attendType: request.attendType,
            comment: request.comment
        }
    )
}